import type { ScanFrequency } from '@/sections/settings/subscription/constant';

interface MSRPInputs {
  numberOfApplications: number;
  scanSchedule: ScanFrequency | string;
  numberOfAssets?: number;
  selectedTier?: string;
  numberOfAEVExercises?: number;
}

interface PricingBreakdown {
  fqdnCost: number;
  assetCost: number;
  aevCost: number;
}

interface MSRPResult {
  floor: number;
  msrp: number;
  breakdown: PricingBreakdown; // MSRP-level breakdown
  floorBreakdown: PricingBreakdown; // Floor-level breakdown
  aevFloor: number;
  aevMsrp: number;
}

// Floor prices (absolute minimum sales cannot go below)
const ASSET_FLOOR_RATE = 3.5; // $3.50/asset
const FQDN_FLOOR_RATE = 1000; // $1,000/application (DAST target)

const AEV_EXERCISE_FLOOR_RATE = 25000; // $25K floor / $50K MSRP per adversary emulation exercise

// MSRP multiplier (2× floor)
const MSRP_MULTIPLIER = 2;

// Frequency multipliers applied to assets and FQDNs
// Weekly scans take 2 days to finish, so floor = 2/7 of daily (0.2857) and MSRP = 0.57 of daily
// Monthly = weekly/4 (1 scan per month): floor = 0.0714, msrp = 0.1425
// Quarterly = weekly/12 (1 scan per quarter): floor = 0.0238, msrp = 0.0475
const FREQUENCY_MULTIPLIERS: Record<string, { floor: number; msrp: number }> = {
  daily: { floor: 1.0, msrp: 1.0 },
  weekly: { floor: 0.2857, msrp: 0.57 },
  monthly: { floor: 0.0714, msrp: 0.1425 },
  quarterly: { floor: 0.0238, msrp: 0.0475 },
};

// Note: This is a pure calculation function despite the `use` prefix.
// The useMemo was removed (React 19 Compiler handles memoization).
// Keeping the hook name for import stability across the codebase.
export function useMSRPCalculator(inputs: MSRPInputs): MSRPResult {
  const multiplier = FREQUENCY_MULTIPLIERS[inputs.scanSchedule] || {
    floor: 1,
    msrp: 1,
  };

  const numberOfAssets = inputs.numberOfAssets || 0;
  const numberOfApplications = inputs.numberOfApplications || 0;

  // Floor calculation: flat rate × count × floor frequency multiplier
  const floorAssetCost = Math.round(
    numberOfAssets * ASSET_FLOOR_RATE * multiplier.floor
  );
  const floorFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * multiplier.floor
  );

  // AEV per-exercise pricing (Ultimate tier only)
  const isUltimate = inputs.selectedTier === 'ultimate';
  const numberOfAEVExercises = isUltimate
    ? inputs.numberOfAEVExercises || 0
    : 0;
  const floorAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE
  );
  const msrpAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE * MSRP_MULTIPLIER
  );

  const floor = floorAssetCost + floorFqdnCost + floorAEVCost;

  // MSRP: flat rate × MSRP_MULTIPLIER × MSRP frequency multiplier
  const msrpAssetCost = Math.round(
    numberOfAssets * ASSET_FLOOR_RATE * MSRP_MULTIPLIER * multiplier.msrp
  );
  const msrpFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * MSRP_MULTIPLIER * multiplier.msrp
  );
  const msrp = msrpAssetCost + msrpFqdnCost + msrpAEVCost;

  return {
    floor,
    msrp,
    breakdown: {
      fqdnCost: msrpFqdnCost,
      assetCost: msrpAssetCost,
      aevCost: msrpAEVCost,
    },
    floorBreakdown: {
      fqdnCost: floorFqdnCost,
      assetCost: floorAssetCost,
      aevCost: floorAEVCost,
    },
    aevFloor: floorAEVCost,
    aevMsrp: msrpAEVCost,
  };
}

// Helper function to format currency
export function formatMSRP(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
